import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Page, alert } from 'equasion-lib';
import { FadeLoader } from 'react-spinners';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronLeft,
  FiList,
  FiMapPin,
  FiNavigation,
  FiSkipForward,
  FiXCircle
} from 'react-icons/fi';
import SharedScanner from '@/lib/shared-coms/SharedScanner';
import ZonePickerModal from '@/lib/shared-coms/ZonePickerModal';
import type { ChosenZone } from '@/lib/shared-coms/ZonePickerModal/ZonePickerModal';
import useZoneScanLookup from '@/lib/shared-coms/ZoneScanner/useZoneScanLookup';
import { SCAN_PROFILE, useScanner } from '@/root/App/com/ScannerProvider/ScannerProvider';
import { playSuccessTone } from '@/lib/scanning/feedback';
import { scanAlert } from '@/lib/scanning/scanAlert';
import { looksLikeProductScan, type ProductScanLike } from '@/lib/scanning/retailCode';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import CleanupLineRow from './com/CleanupLineRow';
import CleanupManualEntry from './com/CleanupManualEntry';
import CleanupPickListModal from './com/CleanupPickListModal';
import type { CleanupResult } from './QuarantineCleanup.data';
import {
  addUnit,
  classifyStopZoneLookup,
  cleanupEventCount,
  clearLine,
  completePicksInput,
  exceedsBudget,
  isLineFullyPicked,
  linePick,
  matchCleanupScan,
  matchTypedCode,
  pickAll,
  pickListSummary,
  setBulkCount,
  stopPickedCount,
  totalPicked,
  unitsLabel,
  type CleanupLine,
  type CleanupList,
  type CleanupPicks,
  type CleanupScanMatch
} from './quarantineCleanupSteps';
import './QuarantineCleanup.scss';

type CleanupLocation = {
  id: string;
  companyName?: string | null;
  address?: {
    lineOne?: string | null;
    city?: string | null;
    state?: string | null;
  } | null;
};

type QuarantineCleanupProps = {
  companyId: string;
  locations: CleanupLocation[];
  locationId: string | null;
  setLocationId: (locationId: string | null) => void;
  list: CleanupList | null;
  loadingList: boolean;
  /** The last list read failed (shown with Try again; the walk never starts on a stale list). */
  listError: unknown;
  onReload: () => Promise<CleanupList | null>;
  onComplete: (
    quarantineZoneId: string,
    picks: ReturnType<typeof completePicksInput>
  ) => Promise<CleanupResult>;
};

type Phase = 'intro' | 'walk' | 'end';

const click = (onClick: () => void) => ({ onClick });

const locationLabel = (location: CleanupLocation) => {
  const { address } = location;
  const cityState = [address?.city, address?.state].filter(Boolean).join(', ');
  return (
    [address?.lineOne, cityState].filter(Boolean).join(' — ') ||
    location.companyName ||
    'Shipping location'
  );
};

/**
 * Quarantine Cleanup: walk every stock zone holding quarantined product — in Pick Path order, or
 * by zone name when no path is set — and consolidate what is picked into ONE quarantine zone. The
 * same stop-by-stop motion as Pick → Verify → Ship's Pick: each stop's zone label is scanned first,
 * then its lines are picked in full (Pick all, or scan the product), in part (the counter, or
 * scanning just the serialized units taken), or the zone is skipped.
 *
 * Nothing is written while walking: the picks are held here, so a skipped zone or an unpicked
 * remainder simply stays where it is. Complete Cleanup asks for the destination — quarantine zones
 * only — and moves exactly what was picked, in one transaction.
 */
function QuarantineCleanup({
  companyId,
  locations,
  locationId,
  setLocationId,
  list,
  loadingList,
  listError,
  onReload,
  onComplete
}: QuarantineCleanupProps) {
  const navigate = useNavigate();
  const { discardScanResult } = useScanner() || {};
  const lookupZoneCode = useZoneScanLookup({
    companyId,
    locationId: locationId || undefined
  });

  const [phase, setPhase] = useState<Phase>('intro');
  const [stopIndex, setStopIndex] = useState(0);
  const [zoneConfirmed, setZoneConfirmed] = useState(false);
  const [zoneCode, setZoneCode] = useState<string | null>(null);
  const [picks, setPicks] = useState<CleanupPicks>({});
  const [checkingZone, setCheckingZone] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [pickListOpen, setPickListOpen] = useState(false);
  const [destinationPickerOpen, setDestinationPickerOpen] = useState(false);
  const [destination, setDestination] = useState<ChosenZone | null>(null);
  const [moving, setMoving] = useState(false);
  const [result, setResult] = useState<CleanupResult | null>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);

  // The scan handler reads these synchronously — two trigger pulls can land before a re-render.
  const picksRef = useRef(picks);
  picksRef.current = picks;
  const checkingRef = useRef(false);

  const stops = list?.stops || [];
  const stop = phase === 'walk' ? stops[stopIndex] || null : null;
  // The stop on screen NOW, for a zone lookup that answers after the picker has moved on.
  const stopZoneIdRef = useRef<string | null>(null);
  stopZoneIdRef.current = stop?.zone.id ?? null;
  const total = totalPicked(picks);
  const summary = pickListSummary(stops, picks);
  const zoneName = stop?.zone.name || '';
  const location = locations.find((l) => l.id === locationId) || null;

  // A scan published while no step was listening (or the zone label still in frame as the step
  // changes) must not be handed to the next step.
  useEffect(() => {
    discardScanResult?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, stopIndex, zoneConfirmed]);

  const updatePicks = (next: CleanupPicks) => {
    picksRef.current = next;
    setPicks(next);
  };

  /**
   * A pick that ADDS to the list, held to what one cleanup can move (the server's own budget): a
   * walk never collects more than Complete will accept. Returns whether it was taken. Clearing and
   * lowering a count always go through `updatePicks`.
   */
  const addPicks = (next: CleanupPicks) => {
    const budget = list?.eventBudget;
    if (
      exceedsBudget(next, budget) &&
      cleanupEventCount(next, budget!) > cleanupEventCount(picksRef.current, budget!)
    ) {
      alert(
        'This cleanup is full',
        'One cleanup cannot move any more than this. Complete it now, then start another cleanup for the rest.',
        8,
        'warning'
      );
      return false;
    }
    updatePicks(next);
    return true;
  };

  const resetWalk = () => {
    setPhase('intro');
    setStopIndex(0);
    setZoneConfirmed(false);
    setZoneCode(null);
    updatePicks({});
    setDestination(null);
    setResult(null);
  };

  const goToStop = (index: number) => {
    setZoneConfirmed(false);
    setZoneCode(null);
    setManualOpen(false);
    if (index >= stops.length) {
      setPhase('end');
      return;
    }
    setStopIndex(index);
  };

  // ───────────────────────────── zone step ─────────────────────────────

  const submitZone = async (code: string, isScan: boolean) => {
    if (!stop || checkingRef.current) return;
    const stopZoneId = stop.zone.id;
    checkingRef.current = true;
    setCheckingZone(true);
    const lookup = await lookupZoneCode(code);
    checkingRef.current = false;
    setCheckingZone(false);
    // The picker skipped (or left) while the label was being looked up: this answer is about a
    // zone they are no longer at, and must not confirm the stop they are at now.
    if (stopZoneIdRef.current !== stopZoneId) return;
    const say = isScan ? scanAlert : alert;
    const outcome = classifyStopZoneLookup(lookup, stop.zone);
    switch (outcome.kind) {
      case 'accepted':
        if (isScan) playSuccessTone();
        setZoneConfirmed(true);
        setZoneCode(code);
        setManualOpen(false);
        return;
      case 'lookupFailed':
        say(
          'Zone lookup failed',
          operatorFacingMessage(
            outcome.error,
            'Could not look up that zone. Please try again.'
          ),
          6,
          'error'
        );
        return;
      case 'wrongZone':
        say(
          'Wrong zone',
          `That is ${outcome.scannedName}. Go to ${zoneName} and scan its label, or skip it.`,
          6,
          'error'
        );
        return;
      default:
        say(
          'Zone not found',
          `No zone matches “${code}”. Scan the label on ${zoneName}.`,
          6,
          'error'
        );
    }
  };

  // ───────────────────────────── pick step ─────────────────────────────

  const applyMatch = (match: CleanupScanMatch, isScan: boolean) => {
    const say = isScan ? scanAlert : alert;
    switch (match.kind) {
      case 'unit': {
        const current = picksRef.current;
        if (linePick(current, match.line).unitIds.includes(match.unit.serializedItemId)) {
          alert(
            'Already picked',
            `Serial ${match.unit.serialNumber} is already on the pick list.`,
            4,
            'info'
          );
          return;
        }
        if (!addPicks(addUnit(current, match.line, match.unit.serializedItemId))) return;
        if (isScan) playSuccessTone();
        return;
      }
      case 'line': {
        const current = picksRef.current;
        if (isLineFullyPicked(current, match.line)) {
          alert(
            'Already picked',
            `${match.line.productName} is already picked in full.`,
            4,
            'info'
          );
          return;
        }
        if (!addPicks(pickAll(current, match.line))) return;
        if (isScan) playSuccessTone();
        setManualOpen(false);
        return;
      }
      case 'ambiguous':
        say(
          'Several lots here',
          `${match.lines[0].productName} has ${match.lines.length} lots in ${zoneName}. Tap Pick all on the lot you are taking.`,
          6,
          'warning'
        );
        return;
      case 'unitNotHere':
        say(
          'Not quarantined here',
          `That unit of ${match.line.productName} is not one of the quarantined units in ${zoneName}. Leave it on the shelf.`,
          6,
          'error'
        );
        return;
      default:
        say(
          'Not on this list',
          `That product is not quarantined in ${zoneName}.`,
          6,
          'error'
        );
    }
  };

  const handleScan = (scan: ProductScanLike) => {
    if (!stop) return;
    const text = scan?.text?.trim() || '';
    if (!zoneConfirmed) {
      if (looksLikeProductScan(scan)) {
        scanAlert(
          'That is a product barcode',
          `Scan the zone label first: ${zoneName}.`,
          5,
          'warning'
        );
        return;
      }
      if (text) submitZone(text, true);
      return;
    }
    if (!looksLikeProductScan(scan)) {
      // Most often the zone label still in frame.
      if (text && text === zoneCode) return;
      alert('Scan the product', 'That is not a product barcode.', 4, 'info');
      return;
    }
    applyMatch(matchCleanupScan(stop.lines, scan), true);
  };

  const submitManual = (text: string) => {
    if (!stop) return;
    if (!zoneConfirmed) {
      submitZone(text, false);
      return;
    }
    applyMatch(matchTypedCode(stop.lines, text), false);
  };

  // ───────────────────────────── complete ─────────────────────────────

  const moveToDestination = async () => {
    if (!destination || moving) return;
    setMoving(true);
    try {
      const moved = await onComplete(
        destination.id,
        completePicksInput(picksRef.current)
      );
      setResult(moved);
      setDestination(null);
    } catch (e) {
      alert(
        'Could not complete the cleanup',
        operatorFacingMessage(e, 'Nothing was moved. Please try again.'),
        8,
        'error'
      );
    } finally {
      setMoving(false);
    }
  };

  const leave = () => {
    if (total > 0 && !result) setConfirmLeave(true);
    else navigate('/');
  };

  // ───────────────────────────── location chooser ─────────────────────────────

  if (!locationId) {
    return (
      <Page className="quarantine-cleanup action-page">
        <div className="action-top-bar">
          <Button
            type="secondary"
            iconBefore={<FiChevronLeft />}
            {...click(() => navigate('/'))}
          >
            Back
          </Button>
          <div className="status">Quarantine Cleanup</div>
        </div>
        <div className="action-page-body">
          <div className="cleanup-intro">
            <h1>Which floor?</h1>
            <p>A quarantine cleanup walks one shipping location.</p>
          </div>
          {locations.length === 0 && (
            <div className="cleanup-empty">
              <FiAlertTriangle />
              <p>
                This company has no shipping location yet, so there are no zones to clean
                up.
              </p>
            </div>
          )}
          <ul className="cleanup-locations">
            {locations.map((l) => (
              <li key={l.id}>
                <button
                  type="button"
                  className="cleanup-location"
                  onClick={() => setLocationId(l.id)}
                >
                  <FiMapPin />
                  <span>{locationLabel(l)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </Page>
    );
  }

  const walking = phase === 'walk' || phase === 'end';

  return (
    <Page className="quarantine-cleanup action-page">
      <div className="action-top-bar">
        <div className="order-details">
          <h1>Quarantine Cleanup</h1>
          <h2>
            {phase === 'walk' && stop
              ? `Zone ${stopIndex + 1} of ${stops.length} · ${unitsLabel(total)} picked`
              : phase === 'end'
              ? `All zones visited · ${unitsLabel(total)} picked`
              : location
              ? locationLabel(location)
              : ''}
          </h2>
        </div>
        {walking && (
          <button
            type="button"
            className="cleanup-pick-list-button"
            onClick={() => setPickListOpen(true)}
          >
            <FiList aria-hidden /> Pick List
            {total > 0 && (
              <span className="cleanup-pick-list-button__count">{total}</span>
            )}
          </button>
        )}
        <FiXCircle aria-label="Close" onClick={leave} />
      </div>

      <div className="action-page-body">
        {phase === 'intro' && (
          <>
            {!list && loadingList && (
              <p className="cleanup-note">Loading quarantined stock…</p>
            )}
            {!list && !loadingList && !!listError && (
              <div className="cleanup-empty">
                <FiAlertTriangle style={{ color: 'var(--red)' }} />
                <p>
                  {operatorFacingMessage(
                    listError,
                    'Could not load the quarantined stock. Check the connection and try again.'
                  )}
                </p>
                <Button type="primary" fullWidth {...click(() => onReload())}>
                  Try again
                </Button>
              </div>
            )}
            {list && (
              <>
                <div
                  className={['walk-bar', !list.pathConfigured && 'walk-bar--none']
                    .filter(Boolean)
                    .join(' ')}
                >
                  <FiNavigation className="walk-bar__icon" />
                  <div className="walk-bar__text">
                    {list.pathConfigured
                      ? 'Zones are in Pick Path order; zones not on the path come last, by name.'
                      : 'No pick path set up for this floor yet, so zones are in name order. Walk it once from the menu: Pick Path Setup.'}
                  </div>
                </div>
                {list.notes.map((note) => (
                  <p key={note} className="cleanup-note cleanup-note--warn">
                    {note}
                  </p>
                ))}
                {stops.length === 0 ? (
                  <div className="cleanup-empty">
                    <FiCheckCircle style={{ color: 'var(--green)' }} />
                    <p>No stock zone holds quarantined product. Nothing to clean up.</p>
                  </div>
                ) : (
                  <>
                    <div className="cleanup-intro">
                      <h1>
                        {unitsLabel(list.totalQuantity)} quarantined in {stops.length}{' '}
                        {stops.length === 1 ? 'stock zone' : 'stock zones'}
                      </h1>
                      <p>
                        Walk each zone, scan its label, and pick what is there. At any
                        point, complete the cleanup by choosing one quarantine zone — only
                        what you picked moves.
                      </p>
                    </div>
                    <ol className="cleanup-stops">
                      {stops.map((s) => (
                        <li key={s.zone.id}>
                          <span className="cleanup-stops__rank">{s.rank + 1}</span>
                          <span className="cleanup-stops__zone">{s.zone.name}</span>
                          <span className="cleanup-stops__qty">
                            {unitsLabel(s.lines.reduce((sum, l) => sum + l.quantity, 0))}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </>
                )}
              </>
            )}
            <div className="cleanup-footer">
              {stops.length > 0 && (
                <Button
                  type="primary"
                  fullWidth
                  {...click(() => {
                    setPhase('walk');
                    goToStop(0);
                  })}
                >
                  Start Pick List
                </Button>
              )}
              {locations.length > 1 && (
                <Button type="secondary" fullWidth {...click(() => setLocationId(null))}>
                  Change location
                </Button>
              )}
            </div>
          </>
        )}

        {phase === 'walk' && stop && (
          <div className="cleanup-stop">
            <div className="cleanup-stop__header">
              <span className="cleanup-stop__rank">Zone {stopIndex + 1}</span>
              <h2>{zoneName}</h2>
              {zoneConfirmed && (
                <span className="cleanup-stop__ok">
                  <FiCheckCircle aria-hidden /> Zone confirmed
                </span>
              )}
            </div>

            {!zoneConfirmed ? (
              <div className="cleanup-stop__prompt">
                <h3>
                  Scan zone <strong>{zoneName}</strong>
                </h3>
                <p>Scan the zone label to confirm you are at the right zone.</p>
                {checkingZone && <p className="cleanup-stop__checking">Checking…</p>}
              </div>
            ) : (
              <div className="cleanup-stop__prompt">
                <p>
                  Scan a product to pick its line in full, scan each serialized unit you
                  take, or set a count for a partial pick.
                </p>
              </div>
            )}

            <div className="cleanup-stop__lines">
              {stop.lines.map((line: CleanupLine) => (
                <CleanupLineRow
                  key={line.zoneInventoryBalanceId}
                  line={line}
                  pick={linePick(picks, line)}
                  interactive={zoneConfirmed}
                  onPickAll={() => addPicks(pickAll(picksRef.current, line))}
                  onSetCount={(count) =>
                    addPicks(setBulkCount(picksRef.current, line, count))
                  }
                  onClear={() => updatePicks(clearLine(picksRef.current, line))}
                />
              ))}
            </div>

            <div className="cleanup-stop__scanner">
              <SharedScanner
                key={`${stop.zone.id}-${zoneConfirmed ? 'product' : 'zone'}`}
                handleScan={handleScan}
                handleManualEntry={() => setManualOpen(true)}
                scanProfile={zoneConfirmed ? SCAN_PROFILE.PRODUCT : SCAN_PROFILE.ZONE}
              />
            </div>

            <div className="cleanup-footer">
              {zoneConfirmed && stopPickedCount(stop, picks) > 0 ? (
                <Button
                  type="primary"
                  fullWidth
                  {...click(() => goToStop(stopIndex + 1))}
                >
                  {stopIndex + 1 < stops.length ? 'Next zone' : 'Finish walk'}
                </Button>
              ) : (
                <button
                  type="button"
                  className="cleanup-skip"
                  onClick={() => goToStop(stopIndex + 1)}
                >
                  <FiSkipForward aria-hidden /> Skip {zoneName}
                </button>
              )}
              <Button
                type="secondary"
                fullWidth
                disabled={total === 0}
                {...click(() => setDestinationPickerOpen(true))}
              >
                Complete Cleanup
              </Button>
            </div>
          </div>
        )}

        {phase === 'end' && (
          <div className="cleanup-end">
            <div className="cleanup-intro">
              <h1>Every zone visited</h1>
              <p>
                {total > 0
                  ? `${unitsLabel(
                      total
                    )} picked. Choose the quarantine zone they are going into.`
                  : 'Nothing was picked, so there is nothing to move.'}
              </p>
            </div>
            <div className="cleanup-footer">
              <Button
                type="primary"
                fullWidth
                disabled={total === 0}
                {...click(() => setDestinationPickerOpen(true))}
              >
                Complete Cleanup
              </Button>
              <Button type="secondary" fullWidth {...click(() => setPickListOpen(true))}>
                Pick List
              </Button>
              {total === 0 && (
                <Button type="secondary" fullWidth {...click(() => navigate('/'))}>
                  Done
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        className={['result-panel', (!!destination || !!result || confirmLeave) && 'open']
          .filter(Boolean)
          .join(' ')}
      >
        {result && (
          <>
            <div className="result-message">
              <FiCheckCircle style={{ color: 'var(--green)' }} />
              <h1>Cleanup complete</h1>
              <p>
                {unitsLabel(result.movedQuantity)} from {result.zoneCount}{' '}
                {result.zoneCount === 1 ? 'zone' : 'zones'} moved into{' '}
                <b>{result.quarantineZone.name}</b>. Anything skipped or not picked stayed
                where it was.
              </p>
            </div>
            <div className="result-actions">
              <Button type="primary" fullWidth {...click(() => navigate('/'))}>
                Done
              </Button>
              <Button
                type="secondary"
                fullWidth
                {...click(async () => {
                  resetWalk();
                  await onReload();
                })}
              >
                Start another cleanup
              </Button>
            </div>
          </>
        )}
        {!result && destination && (
          <>
            <div className="result-message">
              <FiMapPin style={{ color: 'var(--orange)' }} />
              <h1>Move to {destination.name}?</h1>
              <p>
                {unitsLabel(total)} from {summary.length}{' '}
                {summary.length === 1 ? 'zone' : 'zones'} will move into quarantine zone{' '}
                <b>{destination.name}</b>.
              </p>
            </div>
            <div className="result-actions">
              <Button
                type="primary"
                fullWidth
                disabled={moving}
                {...click(moveToDestination)}
              >
                Move to {destination.name}
              </Button>
              <Button
                type="secondary"
                fullWidth
                disabled={moving}
                {...click(() => {
                  setDestination(null);
                  setDestinationPickerOpen(true);
                })}
              >
                Choose another zone
              </Button>
              <Button
                type="secondary"
                fullWidth
                disabled={moving}
                {...click(() => setDestination(null))}
              >
                Keep picking
              </Button>
            </div>
          </>
        )}
        {!result && !destination && confirmLeave && (
          <>
            <div className="result-message">
              <FiAlertTriangle style={{ color: 'var(--orange)' }} />
              <h1>Leave the cleanup?</h1>
              <p>
                Nothing has moved yet. The {unitsLabel(total)} you picked stay recorded in
                their stock zones.
              </p>
            </div>
            <div className="result-actions">
              <Button type="primary" fullWidth {...click(() => setConfirmLeave(false))}>
                Keep picking
              </Button>
              <Button type="secondary" fullWidth {...click(() => navigate('/'))}>
                Leave
              </Button>
            </div>
          </>
        )}
      </div>

      {manualOpen && stop && (
        <CleanupManualEntry
          mode={zoneConfirmed ? 'product' : 'zone'}
          zoneName={zoneName}
          busy={checkingZone}
          onSubmit={submitManual}
          onClose={() => setManualOpen(false)}
        />
      )}

      <CleanupPickListModal
        open={pickListOpen}
        summary={summary}
        total={total}
        onClear={(line) => updatePicks(clearLine(picksRef.current, line))}
        onClose={() => setPickListOpen(false)}
      />

      <ZonePickerModal
        className="cleanup-destination-modal"
        open={destinationPickerOpen}
        onClose={() => setDestinationPickerOpen(false)}
        title="Select the quarantine zone:"
        label="Quarantine zone"
        confirmLabel="Use this zone"
        companyId={companyId}
        locationId={locationId}
        zoneTypes={['quarantine']}
        scanTitle="Scan Quarantine Zone"
        scanHint="Scan the label on the quarantine zone everything picked is going into."
        onZoneChosen={(zone) => setDestination(zone)}
      />

      {moving && (
        <div className="blocking-spinner-container" onClick={(e) => e.stopPropagation()}>
          <FadeLoader color="var(--n0)" />
        </div>
      )}
    </Page>
  );
}

export default QuarantineCleanup;
